import React, { FC, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import usePastTime from 'hooks/usePastTime';
import { Note } from 'models/Note';
import { Button } from 'ncoded-component-library';
import confirm from 'modules/confirm';
import { TextHighlighter } from 'components/TextHighlighter';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import TrashIcon from 'icons/Trash.icon';
import {
  checkIsTextValidJSON,
  checkIsUserSuperAdmin,
  getArrayLength,
  getIsArrayEmpty,
  getIsCurrentUser,
  getJoinedWordsWithComma,
  getUserFullName,
  isSuperAdminOrOwner,
} from 'utils';
import DisplayRichTextString from 'components/RichTextEditorField/components/DisplayRichTextString';
import { GENERAL_BM_ACCOUNT } from 'data/global';
import MessageFile from 'components/MessageFiles/components/MessageFile';
import { MessageFileHandlers } from 'components/MessageFiles/components/MessageFile/MessageFile.component';
import { countTextLengthFromJson } from 'components/RichTextEditorField/utils';
import DealContext from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/providers/Deal/Deal.context';
import { ReactionType, ReactionUsers } from 'models/Reactions';
import { AxiosResponse } from 'axios';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import Reactions from 'components/Reactions';

import './NoteCard.styles.scss';

const OLD_MENTION_REGEX = /@\[(.+?)\]/g;
const MENTION_REGEX = /(@\w+)/g;

type NoteCardProps = {
  className?: string;
  note: Note;
  removeNote: (noteId: string) => Promise<void>;
  setLoading: (value: boolean) => void;
  onReact: (noteId: string, reaction: ReactionType) => void;
  getReactionUsers: (noteId: string) => Promise<AxiosResponse<ReactionUsers>>;
} & MessageFileHandlers;

const NoteCard: FC<NoteCardProps> = (props) => {
  const {
    className,
    note,
    removeNote,
    onFilePreview,
    onFileAddToDataRoom,
    onFileRemove,
    setLoading,
    onReact,
    getReactionUsers,
  } = props;

  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { dealRoom, colleagues } = useContext(DealContext);

  const { name: dealRoomName, dataRoomId, userId: ownerId } = dealRoom;

  const {
    _id: noteId,
    user,
    content,
    createdAt,
    files,
    isInterestContext,
    seenByColleagues = [],
    reactions,
  } = note || {};

  const { _id: userId, firstName, lastName, role, email } = user || {};

  const { t } = useTranslation();

  const canRemoveNote =
    isSuperAdminOrOwner(isSuperAdmin, userId, currentUser?._id) &&
    !isInterestContext;

  const isMyNote = useMemo(() => getIsCurrentUser(userId), [userId]);

  const classes = classNames(
    'note-card',
    'white-panel',
    { 'my-note': isMyNote },
    className,
  );

  const colleaguesForSeenCheck = useMemo(
    () =>
      getArrayLength(colleagues) < 2
        ? []
        : colleagues.filter(
            (colleague) => colleague.userId !== userId && colleague.isAccepted,
          ),
    [colleagues, userId],
  );

  const colleaguesThatSeenNote = useMemo(
    () =>
      colleaguesForSeenCheck.filter(({ userId }) =>
        seenByColleagues.some(
          ({ colleagueUserId }) => colleagueUserId === userId,
        ),
      ),
    [colleaguesForSeenCheck, seenByColleagues],
  );

  const seenByLabel = useMemo(
    () =>
      getIsArrayEmpty(seenByColleagues) ||
      getIsArrayEmpty(colleaguesThatSeenNote)
        ? ''
        : getArrayLength(colleaguesForSeenCheck) ===
          getArrayLength(colleaguesThatSeenNote)
        ? t('seenByEveryone')
        : t('seenByColleagues', {
            colleagues: getJoinedWordsWithComma(
              colleaguesThatSeenNote.map(({ name }) => name),
            ),
          }),
    [colleaguesForSeenCheck, colleaguesThatSeenNote, seenByColleagues, t],
  );

  const pastTime = usePastTime();

  const uplaodedBy = checkIsUserSuperAdmin(role)
    ? t('uploadedByResolve', {
        username: email === GENERAL_BM_ACCOUNT ? 'By Admin' : firstName,
      })
    : `${isInterestContext ? t('interestContextBy') : ''}${getUserFullName(
        firstName,
        lastName,
      )}`;

  const isJson = useMemo(() => checkIsTextValidJSON(content), [content]);

  const isEmpty = useMemo(
    () => (isJson ? !countTextLengthFromJson(content) : !content),
    [isJson, content],
  );

  const showCanReactToNote = useMemo(() => !isMyNote, [isMyNote]);

  const activeReaction = useMemo(() => {
    if (!reactions) return undefined;

    const entry = Object.entries(reactions).find(([, value]) =>
      value?.some(({ userId }) => getIsCurrentUser(userId)),
    );

    return entry ? (entry[0] as ReactionType) : undefined;
  }, [reactions]);

  const renderedFiles = useMemo(
    () =>
      files?.map(
        ({
          _id,
          canMoveFileToDataRoom,
          isDataRoomFile,
          file: { name, originalType, type, url },
        }) => (
          <MessageFile
            key={_id}
            id={_id}
            name={name}
            url={url}
            dataRoomId={dataRoomId}
            dataRoomName={dealRoomName}
            ownerId={ownerId}
            userSide={Q_AND_A_SIDES.SELL}
            type={originalType || type}
            isDataRoomFile={isDataRoomFile}
            canMoveFileToDataRoom={canMoveFileToDataRoom}
            canRemoveFile={canMoveFileToDataRoom}
            onFilePreview={(fileId) => onFilePreview(fileId, noteId)}
            onFileAddToDataRoom={async (fileId) =>
              await onFileAddToDataRoom(fileId, noteId)
            }
            onFileRemove={async (fileId, removeFromDataRoom) =>
              await onFileRemove(fileId, removeFromDataRoom, noteId)
            }
          />
        ),
      ),
    [
      files,
      noteId,
      dealRoomName,
      dataRoomId,
      ownerId,
      onFilePreview,
      onFileAddToDataRoom,
      onFileRemove,
    ],
  );

  const handleOnDelete = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('deleteNote'),
          content: t('confirmDelete'),
          type: 'delete',
          submitBtnText: t('yes'),
          cancelBtnText: t('cancel'),
        },
        true,
      )
    ) {
      try {
        setLoading(true);
        await removeNote?.(noteId);
        popSuccess(t('successfullyRemovedNote'));
      } catch (e) {
        popServerError(e);
      } finally {
        setLoading(false);
      }
    }
  }, [noteId, popServerError, popSuccess, removeNote, setLoading, t]);

  if (isEmpty && !renderedFiles?.length) return null;

  return (
    <div className={classes}>
      {!isEmpty && (
        <div className="note-card__text">
          {isJson ? (
            <DisplayRichTextString>{content}</DisplayRichTextString>
          ) : (
            <TextHighlighter
              text={content.replace(OLD_MENTION_REGEX, '@$1')}
              highlighter={MENTION_REGEX}
            />
          )}
        </div>
      )}
      {!!renderedFiles?.length && (
        <div className="note-card__files">{renderedFiles}</div>
      )}
      <div className="note-card__info">
        <div>
          <div>
            <p>{pastTime(createdAt)}</p>
            <p>{uplaodedBy}</p>
          </div>
          {seenByLabel && <p>{seenByLabel}</p>}
        </div>

        {canRemoveNote && (
          <Button
            aria-label="Remove"
            variant="icon"
            className="warning-action-button"
            icon={TrashIcon}
            onClick={handleOnDelete}
          />
        )}
      </div>

      <Reactions
        thumbsUp={getArrayLength(reactions?.thumbsUp)}
        thumbsDown={getArrayLength(reactions?.thumbsDown)}
        questionMark={getArrayLength(reactions?.questionMark)}
        activeReaction={activeReaction}
        canReact={showCanReactToNote}
        onReact={(reaction) => onReact(noteId, reaction)}
        getReactionUsers={() => getReactionUsers(noteId)}
      />
    </div>
  );
};

export default NoteCard;
